import React from "react";
import {
  Box, Container, Typography, Stack, Button, Snackbar,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Select, MenuItem, InputLabel, FormControl, IconButton,
  Tabs, Tab
} from "@mui/material";
import { Divider } from "@mui/material";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import CloseIcon from "@mui/icons-material/Close";
import { styled } from "@mui/system";
import { useNavigate, useLocation } from "react-router-dom";

/* =================== TYPES =================== */
type PoolItemPack = {
  id: number;
  system: string;     // "collector" / "ahu" / "fan" ...
  rows: any[];
  createdAt?: string;
  meta?: Record<string, any>;
};
type CatalogItem = {
  id: string;                 // sheetName#rowIndex
  sheet?: string;
  brand?: string;
  family?: string;
  measuring?: string;
  model?: string;
  category?: string;
  mounting?: string;
  name?: string;
  code?: string;
  signal?: string;
  raw?: Record<string, any>;
};
type RowSelectionMap = Record<string, CatalogItem[]>;
type SavedPanelRecord = {
  id: number;
  projectKey: string;
  createdAt: string;
  panel: {
    name?: string;
    description?: string;
    located?: string;
    supplyType?: string;
    voltage?: string;
    formType?: string;
    brand?: string;
    color?: string;
  };
};
type AssignmentsMap = Record<string, { panelId: number; panelName: string }>;

/* =================== STYLES =================== */
const ModernButton = styled(Button)({
  borderRadius: '8px',
  padding: '8px 16px',
  fontSize: '0.8125rem',
  fontWeight: 500,
  textTransform: 'none',
  letterSpacing: '0.02em',
  transition: 'all 0.2s ease',
  border: '1px solid',
  '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 1px 2px rgba(0,0,0,0.1)' }
});
const PrimaryButton = styled(ModernButton)({
  backgroundColor: '#1976d2',
  color: '#fff',
  borderColor: 'transparent',
  '&:hover': { backgroundColor: '#1565c0', borderColor: 'transparent' }
});

// Açık zemin
const selectStyles = {
  color: '#2E3A45',
  '.MuiOutlinedInput-notchedOutline': { borderColor: '#CFD8DC' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#B0BEC5' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#90A4AE' },
  '& .MuiSelect-select, & .MuiInputBase-input': { color: '#2E3A45' },
  '& .MuiSvgIcon-root': { color: '#78909C' },
  '&.Mui-disabled': { color: '#888', backgroundColor: '#f5f5f5' },
  '&.Mui-disabled .MuiOutlinedInput-notchedOutline': { borderColor: '#DDDDDD' },
};
const labelStyles = {
  color: '#78909C',
  '&.Mui-focused': { color: '#78909C' },
};

// Koyu zemin
const selectStylesDark = {
  color: '#fff',
  '.MuiOutlinedInput-notchedOutline': { borderColor: '#9E9E9E' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#BDBDBD' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#E0E0E0' },
  '& .MuiSelect-select, & .MuiInputBase-input': { color: '#fff', WebkitTextFillColor: '#fff' },
  '& .MuiSvgIcon-root': { color: '#E0E0E0' },
  '&.Mui-disabled': { color: '#888', backgroundColor: '#1e1e1e' },
  '&.Mui-disabled .MuiOutlinedInput-notchedOutline': { borderColor: '#444' },
};
const labelStylesDark = {
  color: '#BDBDBD',
  '&.Mui-focused': { color: '#E0E0E0' },
};
const textFieldStylesDark = {
  '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': { borderColor: '#9E9E9E' },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#BDBDBD' },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#E0E0E0' },
  '& .MuiInputBase-input': { color: '#fff', WebkitTextFillColor: '#fff' },
  '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline': { borderColor: '#444' },
  '& .MuiInputBase-root.Mui-disabled': { backgroundColor: '#1e1e1e' },
};

/* =================== HELPERS =================== */
const normalize = (s: string) => (s ?? "").trim().toLowerCase();
function sortOriginal(a: any, b: any) {
  const ac = String(a.projectCode || "");
  const bc = String(b.projectCode || "");
  if (ac !== bc) return ac.localeCompare(bc);
  const ta = Number(a.__packCreatedAt || 0);
  const tb = Number(b.__packCreatedAt || 0);
  if (ta !== tb) return ta - tb;
  const oa = typeof a.order === "number" ? a.order : 0;
  const ob = typeof b.order === "number" ? b.order : 0;
  return oa - ob;
}
function buildRowKey(row: any, idx: number): string {
  const parts = [
    String(row.__key ?? ""),
    String(row.__system ?? ""),
    String(row.__packCreatedAt ?? ""),
    String(row.projectCode ?? ""),
    String(typeof row.order === "number" ? row.order : idx),
    String(row.point ?? "")
  ];
  return parts.join("|");
}

/* ===== Heuristik kolon çözümleyiciler ===== */
const BRAND_KEYS = ["brand","marka","manufacturer","firma","make","vendor"];
const CATEGORY_KEYS = ["category","kategori","group","grup","type","tür","product_type","ürün_türü"];
const MOUNT_KEYS = ["mounting","montaj","mounting_type","montaj_tipi","tip","bağlantı"];
const NAME_KEYS = ["name","ürün","product","model","description","açıklama","malzeme","item"];
const CODE_KEYS = ["code","kod","sku","id","product_code","ürün_kodu","item_code"];
const FAMILY_KEYS = ["family","product_family","series","seri","seri_adı","family_name"];
const MEASURING_KEYS = ["measuring","measurement","measure","ölçüm","sensing","sensing_type"];
const MODEL_KEYS = ["model","model_no","model_code","model name","ürün_modeli"];
const SIGNAL_KEYS = ["signal type","signal","signaltype","sinyal","sinyal_tipi","signal_type"];

/* =================== COMPONENT =================== */
export default function PoolPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);

  const projectFilter = (params.get("project") || "").trim();
  const projectKeyForStorage = projectFilter || "__all__";

  const [flatRows, setFlatRows] = React.useState<any[]>([]);
  const [snackbar, setSnackbar] = React.useState<{ open: boolean; msg: string; color?: string }>({
    open: false,
    msg: "",
    color: "#4CAF50",
  });

  /* ---------- MATERIAL EKLERİ ---------- */
  const [materialMode, setMaterialMode] = React.useState(false);
  const [selectorOpen, setSelectorOpen] = React.useState(false);
  const [selectorRowKey, setSelectorRowKey] = React.useState("");
  const [selectorRowInfo, setSelectorRowInfo] = React.useState<any>(null);

  const [catalog, setCatalog] = React.useState<CatalogItem[]>([]);
  const [brandFilter, setBrandFilter] = React.useState("");
  const [familyFilter, setFamilyFilter] = React.useState("");
  const [measuringFilter, setMeasuringFilter] = React.useState("");
  const [signalFilter, setSignalFilter] = React.useState("");
  const [modelFilter, setModelFilter] = React.useState("");

  const importTableInputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => { setFamilyFilter(""); setMeasuringFilter(""); setSignalFilter(""); setModelFilter(""); }, [brandFilter]);
  React.useEffect(() => { setMeasuringFilter(""); setSignalFilter(""); setModelFilter(""); }, [familyFilter]);
  React.useEffect(() => { setSignalFilter(""); setModelFilter(""); }, [measuringFilter]);
  React.useEffect(() => { setModelFilter(""); }, [signalFilter]);

  const [materialsByRow, setMaterialsByRow] = React.useState<RowSelectionMap>({});
  const MATERIALS_KEY = "materials:selections:v1";

  React.useEffect(() => {
    try {
      const raw = localStorage.getItem(MATERIALS_KEY);
      if (raw) {
        const obj = JSON.parse(raw);
        if (obj && typeof obj === "object") setMaterialsByRow(obj);
      }
    } catch {}
  }, []);
  const persistMaterials = React.useCallback((map: RowSelectionMap) => {
    try { localStorage.setItem(MATERIALS_KEY, JSON.stringify(map)); } catch {}
  }, []);

  const totals = React.useMemo(() => {
    const n = (v: any) => {
      const num = typeof v === "number" ? v : parseFloat(v);
      return Number.isFinite(num) ? num : 0;
    };
    const sum = (k:
      | "ai" | "ao" | "di" | "do"
      | "modbusRtu" | "modbusTcp"
      | "bacnetMstp" | "bacnetIp"
      | "mbus"
    ) => flatRows.reduce((acc, r) => acc + n(r[k]), 0);

    return {
      rows: flatRows.length,
      ai: sum("ai"),
      ao: sum("ao"),
      di: sum("di"),
      do: sum("do"),
      modbusRtu: sum("modbusRtu"),
      modbusTcp: sum("modbusTcp"),
      bacnetMstp: sum("bacnetMstp"),
      bacnetIp: sum("bacnetIp"),
      mbus: sum("mbus"),
    };
  }, [flatRows]);

  const handleLogout = () => navigate("/");
  const handleBack = () => navigate("/projects");

  const flattenFromKey = (key: string): any[] => {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    let packs: PoolItemPack[] = [];
    try { packs = JSON.parse(raw) || []; } catch { return []; }
    const rows: any[] = [];
    packs.forEach((pack) => {
      const packCreatedAt = pack.createdAt ? Date.parse(pack.createdAt) : 0;
      (pack.rows || []).forEach((r: any, idx: number) => {
        rows.push({
          __key: key,
          __system: pack.system || "unknown",
          __packCreatedAt: packCreatedAt,
          projectCode: r.projectCode,
          description: r.description,
          location: r.location,
          point: r.point,
          ai: r.ai, ao: r.ao, di: r.di, do: r.do,
          modbusRtu: r.modbusRtu,
          modbusTcp: r.modbusTcp,
          bacnetMstp: r.bacnetMstp,
          bacnetIp: r.bacnetIp,
          mbus: r.mbus,
          order: typeof r.order === "number" ? r.order : idx,
        });
      });
    });
    return rows;
  };

  const loadPool = React.useCallback(() => {
    const keys = Object.keys(localStorage).filter(k => k.startsWith("pool:"));
    if (keys.length === 0) {
      setFlatRows([]);
      setSnackbar({ open: true, msg: "Pool is empty", color: "#4CAF50" });
      return;
    }
    const allRows = keys.flatMap(k => flattenFromKey(k));
    let filtered = allRows;
    if (projectFilter) {
      const want = normalize(projectFilter);
      filtered = allRows.filter(r => normalize(String(r.projectCode || "")) === want);
    }
    if (projectFilter && filtered.length === 0) {
      const allSorted = [...allRows].sort(sortOriginal);
      setFlatRows(allSorted);
      setSnackbar({ open: true, msg: "Pool refreshed (all systems)", color: "#4CAF50" });
      return;
    }
    filtered.sort(sortOriginal);
    setFlatRows(filtered);
    setSnackbar({
      open: true,
      msg: projectFilter ? `Pool refreshed (filtered by "${projectFilter}")` : "Pool refreshed (all systems)",
      color: "#4CAF50"
    });
  }, [projectFilter]);

  /* ---------- CATALOG: Excel yükleme ---------- */
  const onExcelFile = async (file: File) => {
    try {
      const XLSX: any = await import("xlsx");
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const list: CatalogItem[] = [];

      for (const sheetName of wb.SheetNames) {
        const ws = wb.Sheets[sheetName];
        const json = XLSX.utils.sheet_to_json(ws, { defval: "", raw: false }) as Record<string, string>[];

        const rows = json.map((row, i) => {
          const lowerMap: Record<string, string> = {};
          Object.keys(row).forEach(k => { lowerMap[k.toLowerCase().trim()] = row[k]; });

          const findVal = (cands: string[]) => {
            for (const c of cands) if (lowerMap[c] !== undefined) return String(lowerMap[c]).trim();
            return "";
          };

          const brand = findVal(BRAND_KEYS);
          const category = findVal(CATEGORY_KEYS);
          const family = findVal(FAMILY_KEYS) || category;
          const measuring = findVal(MEASURING_KEYS);
          const mounting = findVal(MOUNT_KEYS);
          const model = findVal(MODEL_KEYS);
          const code = findVal(CODE_KEYS);
          const name = findVal(NAME_KEYS) || model || code || "";
          const signal = findVal(SIGNAL_KEYS);

          const item: CatalogItem = {
            id: `${sheetName}#${i}`,
            sheet: sheetName,
            brand,
            family,
            measuring,
            model,
            category,
            mounting,
            name,
            code,
            signal,
            raw: row as any
          };
          return item;
        });

        list.push(...rows);
      }

      setCatalog(list);
      setSnackbar({ open: true, msg: `Excel loaded (${list.length} items)`, color: "#4CAF50" });
    } catch (e) {
      setCatalog([]);
      setSnackbar({ open: true, msg: "Could not read Excel file", color: "#E53935" });
    }
  };

  const filteredCatalog = React.useMemo(() => {
    return catalog.filter(it => {
      if (brandFilter && normalize(it.brand || "") !== normalize(brandFilter)) return false;
      if (familyFilter && normalize(it.family || it.category || "") !== normalize(familyFilter)) return false;
      if (measuringFilter && normalize(it.measuring || "") !== normalize(measuringFilter)) return false;
      if (signalFilter && normalize(it.signal || "") !== normalize(signalFilter)) return false;
      if (modelFilter && normalize(it.model || it.code || it.name || "") !== normalize(modelFilter)) return false;
      return true;
    });
  }, [catalog, brandFilter, familyFilter, measuringFilter, signalFilter, modelFilter]);

  const distinct = (arr: string[]) =>
    Array.from(new Set(arr.map(x => (x || "").trim()).filter(Boolean))).sort((a,b) => a.localeCompare(b, "tr"));

  const scopedByBrand = React.useMemo(
    () => (brandFilter ? catalog.filter(it => normalize(it.brand || "") === normalize(brandFilter)) : catalog),
    [catalog, brandFilter]
  );
  const scopedByFamily = React.useMemo(
    () => (familyFilter ? scopedByBrand.filter(it => normalize(it.family || it.category || "") === normalize(familyFilter)) : scopedByBrand),
    [scopedByBrand, familyFilter]
  );
  const scopedByMeasuring = React.useMemo(
    () => (measuringFilter ? scopedByFamily.filter(it => normalize(it.measuring || "") === normalize(measuringFilter)) : scopedByFamily),
    [scopedByFamily, measuringFilter]
  );
  const scopedBySignal = React.useMemo(
    () => (signalFilter ? scopedByMeasuring.filter(it => normalize(it.signal || "") === normalize(signalFilter)) : scopedByMeasuring),
    [scopedByMeasuring, signalFilter]
  );

  const distinctBrands = React.useMemo(() => distinct(catalog.map(c => c.brand || "")), [catalog]);
  const distinctFamilies = React.useMemo(() => distinct(scopedByBrand.map(c => c.family || c.category || "")), [scopedByBrand]);
  const distinctMeasurings = React.useMemo(() => distinct(scopedByFamily.map(c => c.measuring || "")), [scopedByFamily]);
  const distinctSignals = React.useMemo(() => distinct(scopedByMeasuring.map(c => c.signal || "")), [scopedByMeasuring]);
  const distinctModels = React.useMemo(() => distinct(scopedBySignal.map(c => c.model || c.code || c.name || "")), [scopedBySignal]);

  /* ---------- Satır tıklama: Material selector ---------- */
  const openSelectorFor = (row: any, idx: number) => {
    if (!materialMode) return;
    const key = buildRowKey(row, idx);
    setSelectorRowKey(key);
    setSelectorRowInfo(row);
    setSelectorOpen(true);
  };
  const closeSelector = () => {
    setSelectorOpen(false);
    setSelectorRowKey("");
    setSelectorRowInfo(null);
  };

  const toggleMaterialMode = () => {
    setMaterialMode(m => {
      const next = !m;
      setSnackbar({
        open: true,
        msg: next ? "Material column visible" : "Material column hidden",
        color: "#4CAF50"
      });
      return next;
    });
  };

  const selectedForRow = (rowKey: string): CatalogItem[] => materialsByRow[rowKey] || [];
  const saveSelectionForRow = (rowKey: string, items: CatalogItem[]) => {
    const next: RowSelectionMap = { ...materialsByRow, [rowKey]: items };
    setMaterialsByRow(next);
    persistMaterials(next);
    setSnackbar({ open: true, msg: "Materials linked to row", color: "#4CAF50" });
  };
  const renderMaterialCell = React.useCallback((rowKey: string) => {
    const items = selectedForRow(rowKey);
    if (!items.length) return <em style={{ color: '#888' }}>—</em>;
    const label = (it: CatalogItem) => (it.name || it.code || it.model || it.brand || "item");
    return <span>{items.map(label).join(", ")}</span>;
  }, [materialsByRow]);
  const labelOf = (it: CatalogItem) => (it.name || it.code || it.model || it.brand || "item");

  const exportTableToExcel = async () => {
    try {
      const XLSX: any = await import("xlsx");
      const data = flatRows.map((row, idx) => {
        const rowKey = buildRowKey(row, idx);
        const items = (materialsByRow[rowKey] || []);
        return {
          System: row.__system ?? "",
          ProjectCode: row.projectCode ?? "",
          Description: row.description ?? "",
          Located: row.location ?? "",
          Point: row.point ?? "",
          MaterialNames: items.map(labelOf).join("; "),
          AI: row.ai ?? 0,
          AO: row.ao ?? 0,
          DI: row.di ?? 0,
          DO: row.do ?? 0,
          ModbusRTU: row.modbusRtu ?? 0,
          ModbusTCP: row.modbusTcp ?? 0,
          BacnetMSTP: row.bacnetMstp ?? 0,
          BacnetIP: row.bacnetIp ?? 0,
          Mbus: row.mbus ?? 0,
        };
      });
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Pool");
      const d = new Date();
      const pad = (n:number)=>String(n).padStart(2,'0');
      const fname = `PoolExport_${d.getFullYear()}${pad(d.getMonth()+1)}${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}.xlsx`;
      XLSX.writeFile(wb, fname);
      setSnackbar({ open: true, msg: "Exported Excel", color: "#4CAF50" });
    } catch {
      setSnackbar({ open: true, msg: "Export failed", color: "#E53935" });
    }
  };

  const onImportTableFile = async (file: File) => {
    try {
      const XLSX: any = await import("xlsx");
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(ws, { defval: "", raw: false }) as Record<string, any>[];
      const lc = (k:string) => k.toLowerCase().replace(/\s+/g, "");

      const rows: any[] = [];
      const matMap: RowSelectionMap = {};

      json.forEach((r, i) => {
        const m: Record<string, any> = {};
        Object.keys(r).forEach(k => { m[lc(k)] = r[k]; });

        const row: any = {
          __key: "import",
          __system: m["system"] || "",
          __packCreatedAt: Date.now(),
          projectCode: m["projectcode"] ?? m["project"] ?? "",
          description: m["description"] || "",
          location: m["location"] || "",
          point: m["point"] || "",
          ai: Number(m["ai"]||0), ao: Number(m["ao"]||0),
          di: Number(m["di"]||0), do: Number(m["do"]||0),
          modbusRtu: Number(m["modbusrtu"]||0), modbusTcp: Number(m["modbustcp"]||0),
          bacnetMstp: Number(m["bacnetmstp"]||0), bacnetIp: Number(m["bacnetip"]||0),
          mbus: Number(m["mbus"]||0),
          order: i,
        };
        rows.push(row);
      });

      rows.sort(sortOriginal);
      setFlatRows(rows);
      setMaterialsByRow(matMap);
      persistMaterials(matMap);
      setMaterialMode(true);
      setSnackbar({ open: true, msg: `Imported ${rows.length} rows`, color: "#4CAF50" });
    } catch {
      setSnackbar({ open: true, msg: "Import failed", color: "#E53935" });
    }
  };

  /* ========== ASSIGNMENTS ========== */
  const [assignOpen, setAssignOpen] = React.useState(false);
  const [assignTab, setAssignTab] = React.useState(0); // 0: Assignments, 1: Panels, 2: Hardware
  const [panels, setPanels] = React.useState<SavedPanelRecord[]>([]);
  const [assignments, setAssignments] = React.useState<AssignmentsMap>({});
  const [selectedCodes, setSelectedCodes] = React.useState<string[]>([]);
  const [bulkPanelId, setBulkPanelId] = React.useState<string>("");
  const [systemFilter, setSystemFilter] = React.useState<string>("");

  const ASSIGN_KEY = `assignments:${projectKeyForStorage}`;

  const loadPanels = React.useCallback(() => {
    const want = (projectFilter || "").trim();
    const keys = Object.keys(localStorage).filter(k => k.startsWith("panels:"));
    const targetKeys = want ? keys.filter(k => k === `panels:${want}`) : keys;

    const list: SavedPanelRecord[] = [];
    for (const k of targetKeys) {
      try {
        const arr = JSON.parse(localStorage.getItem(k) || "[]");
        if (Array.isArray(arr)) {
          for (const rec of arr) {
            if (rec && typeof rec === "object" && rec.panel) list.push(rec as SavedPanelRecord);
          }
        }
      } catch {}
    }
    list.sort((a, b) => (Date.parse(a.createdAt) || 0) - (Date.parse(b.createdAt) || 0));
    setPanels(list);
  }, [projectFilter]);

  const loadAssignments = React.useCallback(() => {
    try {
      const raw = localStorage.getItem(ASSIGN_KEY);
      const obj = raw ? JSON.parse(raw) : {};
      if (obj && typeof obj === "object") setAssignments(obj as AssignmentsMap);
      else setAssignments({});
    } catch {
      setAssignments({});
    }
  }, [ASSIGN_KEY]);

  const persistAssignments = React.useCallback((next: AssignmentsMap) => {
    try { localStorage.setItem(ASSIGN_KEY, JSON.stringify(next)); } catch {}
  }, [ASSIGN_KEY]);

  const openAssignDialog = () => {
    setAssignTab(0);
    setSelectedCodes([]);
    setBulkPanelId("");
    loadPanels();
    loadAssignments();
    setAssignOpen(true);
  };
  const closeAssignDialog = () => setAssignOpen(false);

  const codeGroups = React.useMemo(() => {
    const map = new Map<string, { count: number; systems: Set<string> }>();
    for (const r of flatRows) {
      const code = String(r.projectCode || "").trim();
      if (!code) continue;
      const s = String(r.__system || "unknown").trim();
      if (!map.has(code)) map.set(code, { count: 0, systems: new Set<string>() });
      const entry = map.get(code)!;
      entry.count += 1;
      entry.systems.add(s);
    }
    const result = Array.from(map.entries())
      .map(([code, v]) => ({ code, count: v.count, systems: Array.from(v.systems).sort() }))
      .sort((a, b) => a.code.localeCompare(b.code, "tr"));
    return result.filter(g => !systemFilter || g.systems.includes(systemFilter));
  }, [flatRows, systemFilter]);

  const distinctSystems = React.useMemo(() => {
    const set = new Set<string>();
    for (const r of flatRows) set.add(String(r.__system || "unknown").trim());
    return Array.from(set).sort((a,b)=>a.localeCompare(b,"tr"));
  }, [flatRows]);

  const handleToggleCode = (code: string, checked: boolean) => {
    setSelectedCodes(prev => {
      const s = new Set(prev);
      if (checked) s.add(code); else s.delete(code);
      return Array.from(s);
    });
  };

  const handleAssignSingle = (code: string, panelIdStr: string) => {
    const idNum = Number(panelIdStr);
    if (!Number.isFinite(idNum)) {
      const next = { ...assignments };
      delete next[code];
      setAssignments(next);
      persistAssignments(next);
      setSnackbar({ open: true, msg: `Cleared assignment for ${code}`, color: "#4CAF50" });
      return;
    }
    const p = panels.find(x => x.id === idNum);
    if (!p) return;
    const next = { ...assignments, [code]: { panelId: p.id, panelName: p.panel.name || `#${p.id}` } };
    setAssignments(next);
    persistAssignments(next);
    setSnackbar({ open: true, msg: `Assigned ${code} → ${p.panel.name || `#${p.id}`}`, color: "#4CAF50" });
  };

  const handleClearSingle = (code: string) => {
    const next = { ...assignments };
    delete next[code];
    setAssignments(next);
    persistAssignments(next);
    setSnackbar({ open: true, msg: `Cleared assignment for ${code}`, color: "#4CAF50" });
  };

  const handleBulkApply = () => {
    const idNum = Number(bulkPanelId);
    if (!Number.isFinite(idNum) || selectedCodes.length === 0) return;
    const p = panels.find(x => x.id === idNum);
    if (!p) return;
    const next = { ...assignments };
    for (const code of selectedCodes) {
      next[code] = { panelId: p.id, panelName: p.panel.name || `#${p.id}` };
    }
    setAssignments(next);
    persistAssignments(next);
    setSnackbar({ open: true, msg: `Assigned ${selectedCodes.length} code(s) → ${p.panel.name || `#${p.id}`}`, color: "#4CAF50" });
  };

  const deletePanel = (id: number, projectKey: string) => {
    const key = `panels:${projectKey}`;
    const arr = JSON.parse(localStorage.getItem(key) || "[]");
    const next = Array.isArray(arr) ? arr.filter((r: any) => r?.id !== id) : [];
    localStorage.setItem(key, JSON.stringify(next));
    setSnackbar({ open: true, msg: "Panel deleted", color: "#4CAF50" });
    loadPanels();
  };

  const dialogExcelInputRef = React.useRef<HTMLInputElement>(null);

  /* =================== RENDER =================== */
  return (
    <Box sx={{ minHeight: "100vh", background: "radial-gradient(circle at top right, #1A237E, #000000)", color: "#FFFFFF", display: "flex", flexDirection: "column" }}>
      {/* Navbar */}
      <Box sx={{ py: 2, px: 4, display: "flex", justifyContent: "space-between", alignItems: "center", backgroundColor: "white", boxShadow: "0 2px 10px rgba(0,0,0,0.08)" }}>
        <Typography variant="body1" sx={{ fontWeight: "normal", fontSize: "1.1rem" }}>
          <Box component="span" sx={{ color: "#1976d2" }}>hava</Box>
          <Box component="span" sx={{ color: "#B0BEC5" }}>logic</Box>
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          <PrimaryButton startIcon={<ExitToAppIcon sx={{ fontSize: "1rem" }} />} onClick={handleLogout} sx={{ minWidth: "100px" }}>
            Logout
          </PrimaryButton>
        </Stack>
      </Box>

      {/* Başlık + özet */}
      <Container sx={{ flex: 1, py: 4 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ xs: "flex-start", md: "center" }}
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Pool {projectFilter ? `– ${projectFilter}` : "– All Systems"}
          </Typography>

          <Stack direction="row" spacing={1.2} flexWrap="wrap">
            <StatPill label="Rows" value={totals.rows} />
            <StatPill label="AI" value={totals.ai} />
            <StatPill label="AO" value={totals.ao} />
            <StatPill label="DI" value={totals.di} />
            <StatPill label="DO" value={totals.do} />
            <StatPill label="Modbus RTU" value={totals.modbusRtu} />
            <StatPill label="Modbus TCP/IP" value={totals.modbusTcp} />
            <StatPill label="Bacnet MSTP" value={totals.bacnetMstp} />
            <StatPill label="Bacnet IP" value={totals.bacnetIp} />
            <StatPill label="Mbus" value={totals.mbus} />
          </Stack>
        </Stack>

        {/* Tablo */}
        <Box sx={{ backgroundColor: "rgba(255,255,255,0.1)", borderRadius: "12px", p: 2, minHeight: "70vh", overflow: "auto" }}>
          {flatRows.length === 0 ? (
            <Box sx={{ height: "100%", minHeight: "50vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                Click <strong>Refresh</strong> to load data.
              </Typography>
            </Box>
          ) : (
            <table
              style={{
                width: "100%",
                minWidth: materialMode ? "1320px" : "1200px",
                borderCollapse: "collapse",
                backgroundColor: "#ffffff",
                fontSize: "0.875rem",
                fontFamily: "'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif",
                borderRadius: "12px",
                overflow: "hidden",
                color: "#333",
              }}
            >
              <thead>
                <tr style={{ backgroundColor: "#1976d2", color: "white" }}>
                  {["System","Project Code","Description","Located","Point Name"].map((h) => (
                    <th key={h} style={{ padding: "10px", textAlign: "left" }}>{h}</th>
                  ))}
                  {materialMode && <th style={{ padding: "10px", textAlign: "left" }}>Material</th>}
                  {["AI","AO","DI","DO","Modbus RTU","Modbus TCP IP","Bacnet MSTP","Bacnet IP","Mbus"].map((h) => (
                    <th key={h} style={{ padding: "10px", textAlign: "left" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {flatRows.map((row, idx) => {
                  const rowKey = buildRowKey(row, idx);
                  return (
                    <tr
                      key={idx}
                      style={{ backgroundColor: idx % 2 === 0 ? "#f9f9f9" : "#f0f4f8", cursor: materialMode ? "pointer" : "default" }}
                      onClick={() => openSelectorFor(row, idx)}
                      title={materialMode ? "Click to select materials for this row" : ""}
                    >
                      <td style={{ padding: 8 }}>{row.__system ?? "collector"}</td>
                      <td style={{ padding: 8 }}>{row.projectCode}</td>
                      <td style={{ padding: 8 }}>{row.description}</td>
                      <td style={{ padding: 8 }}>{row.location}</td>
                      <td style={{ padding: 8 }}>{row.point}</td>
                      {materialMode && (<td style={{ padding: 8 }}>{renderMaterialCell(rowKey)}</td>)}
                      <td style={{ padding: 8 }}>{row.ai}</td>
                      <td style={{ padding: 8 }}>{row.ao}</td>
                      <td style={{ padding: 8 }}>{row.di}</td>
                      <td style={{ padding: 8 }}>{row.do}</td>
                      <td style={{ padding: 8 }}>{row.modbusRtu}</td>
                      <td style={{ padding: 8 }}>{row.modbusTcp}</td>
                      <td style={{ padding: 8 }}>{row.bacnetMstp}</td>
                      <td style={{ padding: 8 }}>{row.bacnetIp}</td>
                      <td style={{ padding: 8 }}>{row.mbus}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </Box>
      </Container>

      {/* Alt buton barı */}
      <Box sx={{ p: 2, borderTop: "1px solid rgba(255,255,255,0.1)", display: "flex", justifyContent: "flex-end", gap: 2, flexWrap: "wrap" }}>
        <PrimaryButton onClick={toggleMaterialMode}>
          {materialMode ? 'Hide Material' : 'Choose Material'}
        </PrimaryButton>
        <PrimaryButton onClick={loadPool}>Refresh</PrimaryButton>
        <PrimaryButton onClick={() => {
          Object.keys(localStorage).forEach((k) => { if (k.startsWith("pool:")) localStorage.removeItem(k); });
          setFlatRows([]);
          setSnackbar({ open: true, msg: "Entire pool cleared", color: "#4CAF50" });
        }}>Clear All Pool</PrimaryButton>
        <PrimaryButton onClick={async () => {
          try {
            await navigator.clipboard.writeText(JSON.stringify(flatRows, null, 2));
            setSnackbar({ open: true, msg: "Copied to clipboard", color: "#4CAF50" });
          } catch {
            setSnackbar({ open: true, msg: "Copy failed (browser blocked clipboard)", color: "#E53935" });
          }
        }} disabled={flatRows.length === 0}>Copy</PrimaryButton>

        <PrimaryButton onClick={exportTableToExcel} disabled={flatRows.length === 0}>Export</PrimaryButton>
        <input
          ref={importTableInputRef}
          type="file"
          hidden
          accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onImportTableFile(f);
            (e.target as HTMLInputElement).value = "";
          }}
        />
        <PrimaryButton onClick={() => importTableInputRef.current?.click()}>Import</PrimaryButton>

        <PrimaryButton onClick={openAssignDialog}>Assign to Panel</PrimaryButton>
        <PrimaryButton onClick={handleBack}>Back</PrimaryButton>
      </Box>

      {/* Material Selector Dialog */}
      <Dialog open={selectorOpen} onClose={closeSelector} fullWidth maxWidth="lg">
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          Select Material
          <IconButton onClick={closeSelector} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {/* Filtreler */}
<Stack
  direction={{ xs: "column", md: "row" }}
  spacing={2}
  alignItems={{ xs: "stretch", md: "center" }}
  sx={{ mb: 2, flexWrap: 'wrap' }}
>
  <FormControl size="small" sx={{ minWidth: 180 }}>
    <InputLabel sx={labelStylesDark}>Brand</InputLabel>
    <Select
      label="Brand"
      value={brandFilter}
      onChange={(e) => setBrandFilter(String(e.target.value))}
      sx={selectStylesDark}
      MenuProps={{ PaperProps: { sx: { bgcolor: '#1e1e1e', color: '#fff' } } }}
    >
      <MenuItem value="">(All)</MenuItem>
      {distinctBrands.map(b => <MenuItem key={b} value={b}>{b}</MenuItem>)}
    </Select>
  </FormControl>

  <FormControl size="small" sx={{ minWidth: 180 }}>
    <InputLabel sx={labelStylesDark}>Family</InputLabel>
    <Select
      label="Family"
      value={familyFilter}
      onChange={(e) => setFamilyFilter(String(e.target.value))}
      sx={selectStylesDark}
      MenuProps={{ PaperProps: { sx: { bgcolor: '#1e1e1e', color: '#fff' } } }}
    >
      <MenuItem value="">(All)</MenuItem>
      {distinctFamilies.map(f => <MenuItem key={f} value={f}>{f}</MenuItem>)}
    </Select>
  </FormControl>

  <FormControl size="small" sx={{ minWidth: 180 }}>
    <InputLabel sx={labelStylesDark}>Measuring</InputLabel>
    <Select
      label="Measuring"
      value={measuringFilter}
      onChange={(e) => setMeasuringFilter(String(e.target.value))}
      sx={selectStylesDark}
      MenuProps={{ PaperProps: { sx: { bgcolor: '#1e1e1e', color: '#fff' } } }}
    >
      <MenuItem value="">(All)</MenuItem>
      {distinctMeasurings.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
    </Select>
  </FormControl>

  <FormControl size="small" sx={{ minWidth: 180 }}>
    <InputLabel sx={labelStylesDark}>Signal Type</InputLabel>
    <Select
      label="Signal Type"
      value={signalFilter}
      onChange={(e) => setSignalFilter(String(e.target.value))}
      sx={selectStylesDark}
      MenuProps={{ PaperProps: { sx: { bgcolor: '#1e1e1e', color: '#fff' } } }}
    >
      <MenuItem value="">(All)</MenuItem>
      {distinctSignals.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
    </Select>
  </FormControl>

  <FormControl size="small" sx={{ minWidth: 180 }}>
    <InputLabel sx={labelStylesDark}>Model</InputLabel>
    <Select
      label="Model"
      value={modelFilter}
      onChange={(e) => setModelFilter(String(e.target.value))}
      sx={selectStylesDark}
      MenuProps={{ PaperProps: { sx: { bgcolor: '#1e1e1e', color: '#fff' } } }}
    >
      <MenuItem value="">(All)</MenuItem>
      {distinctModels.map(mo => <MenuItem key={mo} value={mo}>{mo}</MenuItem>)}
    </Select>
  </FormControl>
</Stack>

          {/* Katalog listesi */}
          <Box sx={{ border: "1px solid #eee", borderRadius: 1, overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, fontFamily: "'Segoe UI','Roboto','Helvetica','Arial',sans-serif", color: "#333", backgroundColor: "#ffffff" }}>
              <thead>
                <tr style={{ backgroundColor: "#1976d2", color: "white" }}>
                  {["Select","Brand","Family","Measuring","Signal Type","Model","Mounting"].map(h => (
                    <th key={h} style={{ padding: "8px", textAlign: "left", borderBottom: "1px solid #e0e0e0" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredCatalog.length === 0 ? (
                  <tr><td colSpan={7} style={{ padding: 12, color: "#777" }}>List is empty. Upload an Excel file or clear filters.</td></tr>
                ) : filteredCatalog.map((it, i) => {
                  const curr = selectedForRow(selectorRowKey);
                  const checked = !!curr.find(c => c.id === it.id);
                  return (
                    <tr key={it.id} style={{ backgroundColor: checked ? "#e3f2fd" : (i % 2 === 0 ? "#f9f9f9" : "#f0f4f8") }}>
                      <td style={{ padding: 6, borderBottom: "1px solid #f0f0f0" }}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            const prev = selectedForRow(selectorRowKey);
                            let next: CatalogItem[];
                            if (e.target.checked) next = [...prev, it];
                            else next = prev.filter(x => x.id !== it.id);
                            saveSelectionForRow(selectorRowKey, next);
                          }}
                        />
                      </td>
                      <td style={{ padding: 6, borderBottom: "1px solid #f0f0f0" }}>{it.brand || "—"}</td>
                      <td style={{ padding: 6, borderBottom: "1px solid #f0f0f0" }}>{it.family || it.category || "—"}</td>
                      <td style={{ padding: 6, borderBottom: "1px solid #f0f0f0" }}>{it.measuring || "—"}</td>
                      <td style={{ padding: 6, borderBottom: "1px solid #f0f0f0" }}>{it.signal || "—"}</td>
                      <td style={{ padding: 6, borderBottom: "1px solid #f0f0f0" }}>{it.model || "—"}</td>
                      <td style={{ padding: 6, borderBottom: "1px solid #f0f0f0" }}>{it.mounting || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, gap: 1.5 }}>
          <input
            ref={dialogExcelInputRef}
            type="file"
            hidden
            accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) onExcelFile(f); (e.target as HTMLInputElement).value = ""; }}
          />
          <ModernButton onClick={() => dialogExcelInputRef.current?.click()}>Upload Excel</ModernButton>
          <PrimaryButton onClick={closeSelector}>Close</PrimaryButton>
        </DialogActions>
      </Dialog>

      {/* Assignments Wizard */}
      <Dialog open={assignOpen} onClose={closeAssignDialog} fullWidth maxWidth="lg">
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          Assign to Panel {projectFilter ? `– ${projectFilter}` : ""}
          <IconButton onClick={closeAssignDialog} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Tabs value={assignTab} onChange={(_, v) => setAssignTab(v)} sx={{ mb: 2 }}>
            <Tab label="Assignments" sx={{ textTransform: 'none', fontWeight: 600 }} />
            <Tab label="Panels" sx={{ textTransform: 'none', fontWeight: 600 }} />
            <Tab label="Hardware (Controller & IO)" sx={{ textTransform: 'none', fontWeight: 600 }} />
          </Tabs>

          {assignTab === 0 && (
            <Box>
              {/* Üst bar: System Filter + Bulk assign */}
              <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ xs: "stretch", md: "center" }} sx={{ mb: 2 }}>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel sx={labelStylesDark}>System Filter</InputLabel>
                  <Select
                    label="System Filter"
                    value={systemFilter}
                    onChange={(e)=>setSystemFilter(String(e.target.value))}
                    sx={selectStylesDark}
                    MenuProps={{ PaperProps: { sx: { bgcolor: '#1e1e1e', color: '#fff' } } }}
                  >
                    <MenuItem value="">(All)</MenuItem>
                    {distinctSystems.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                  </Select>
                </FormControl>

                <Box sx={{ flexGrow: 1 }} />

                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <InputLabel sx={labelStylesDark}>Assign selected to</InputLabel>
                  <Select
                    label="Assign selected to"
                    value={bulkPanelId}
                    onChange={(e)=>setBulkPanelId(String(e.target.value))}
                    sx={selectStylesDark}
                    MenuProps={{ PaperProps: { sx: { bgcolor: '#1e1e1e', color: '#fff' } } }}
                  >
                    <MenuItem value="">(Choose Panel)</MenuItem>
                    {panels.map(p => (
                      <MenuItem key={p.id} value={String(p.id)}>{p.panel.name || `#${p.id}`}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <PrimaryButton onClick={handleBulkApply} disabled={selectedCodes.length === 0 || !bulkPanelId}>
                  Apply
                </PrimaryButton>
              </Stack>

              {panels.length === 0 && (
                <Box sx={{ mb: 2, p: 1.5, border: "1px dashed #B0BEC5", borderRadius: 1, bgcolor: "#fafafa", color: "#333" }}>
                  No panels found. Use the <strong>Panels</strong> tab to create panels in Project page and return here.
                </Box>
              )}

              {/* Project Code grouped table */}
              <Box sx={{ border: "1px solid #eee", borderRadius: 1, overflow: "hidden" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, fontFamily: "'Segoe UI','Roboto','Helvetica','Arial',sans-serif", color: "#333", backgroundColor: "#fff" }}>
                  <thead>
                    <tr style={{ backgroundColor: "#1976d2", color: "white" }}>
                      {["Select","Project Code","Systems","Points","Assigned Panel","Actions"].map(h => (
                        <th key={h} style={{ padding: 8, textAlign: "left", borderBottom: "1px solid #e0e0e0" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {codeGroups.length === 0 ? (
                      <tr><td colSpan={6} style={{ padding: 12, color: "#777" }}>No project codes found. Use <strong>Refresh</strong> or add systems to Pool.</td></tr>
                    ) : codeGroups.map((g, i) => {
                      const assigned = assignments[g.code];
                      const assignedIdStr = assigned ? String(assigned.panelId) : "";
                      return (
                        <tr key={g.code} style={{ backgroundColor: i % 2 === 0 ? "#f9f9f9" : "#f0f4f8" }}>
                          <td style={{ padding: 6, borderBottom: "1px solid #f0f0f0" }}>
                            <input type="checkbox" checked={selectedCodes.includes(g.code)} onChange={(e)=>handleToggleCode(g.code, e.target.checked)} />
                          </td>
                          <td style={{ padding: 6, borderBottom: "1px solid #f0f0f0", fontWeight: 600 }}>{g.code}</td>
                          <td style={{ padding: 6, borderBottom: "1px solid #f0f0f0" }}>{g.systems.join(", ")}</td>
                          <td style={{ padding: 6, borderBottom: "1px solid #f0f0f0" }}>{g.count}</td>
                          <td style={{ padding: 6, borderBottom: "1px solid #f0f0f0" }}>
                            <FormControl size="small" sx={{ minWidth: 200 }}>
                              <Select
                                value={assignedIdStr}
                                onChange={(e)=>handleAssignSingle(g.code, String(e.target.value))}
                                displayEmpty
                                sx={selectStyles}
                              >
                                <MenuItem value="">{assigned ? "(Clears assignment)" : "(None)"}</MenuItem>
                                {panels.map(p => (<MenuItem key={p.id} value={String(p.id)}>{p.panel.name || `#${p.id}`}</MenuItem>))}
                              </Select>
                            </FormControl>
                          </td>
                          <td style={{ padding: 6, borderBottom: "1px solid #f0f0f0" }}>
                            <PrimaryButton onClick={()=>handleClearSingle(g.code)}>Clear</PrimaryButton>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Box>
            </Box>
          )}

          {assignTab === 1 && (
            <Box>
              <Box sx={{ border: "1px solid #eee", borderRadius: 1, overflow: "hidden" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, fontFamily: "'Segoe UI','Roboto','Helvetica','Arial',sans-serif", color: "#333", backgroundColor: "#ffffff" }}>
                  <thead>
                    <tr style={{ backgroundColor: "#1976d2", color: "white" }}>
                      {["Panel Name","Located","Supply Type","Voltage","Form Type","Brand","Color","Actions"].map(h => (
                        <th key={h} style={{ padding: "8px", textAlign: "left", borderBottom: "1px solid #e0e0e0" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {panels.length === 0 ? (
                      <tr><td colSpan={8} style={{ padding: 12, color: "#777" }}>No panels found. Use Project → Create Panel to add.</td></tr>
                    ) : panels.map((p, i) => (
                      <tr key={p.id} style={{ backgroundColor: i % 2 === 0 ? "#f9f9f9" : "#f0f4f8" }}>
                        <td style={{ padding: 6, borderBottom: "1px solid #f0f0f0" }}>{p.panel.name || "—"}</td>
                        <td style={{ padding: 6, borderBottom: "1px solid #f0f0f0" }}>{p.panel.located || "—"}</td>
                        <td style={{ padding: 6, borderBottom: "1px solid #f0f0f0" }}>{p.panel.supplyType || "—"}</td>
                        <td style={{ padding: 6, borderBottom: "1px solid #f0f0f0" }}>{p.panel.voltage || "—"}</td>
                        <td style={{ padding: 6, borderBottom: "1px solid " + "#f0f0f0" }}>{p.panel.formType || "—"}</td>
                        <td style={{ padding: 6, borderBottom: "1px solid #f0f0f0" }}>{p.panel.brand || "—"}</td>
                        <td style={{ padding: 6, borderBottom: "1px solid #f0f0f0" }}>{p.panel.color || "—"}</td>
                        <td style={{ padding: 6, borderBottom: "1px solid #f0f0f0" }}>
                          <PrimaryButton onClick={() => deletePanel(p.id, p.projectKey)}>Delete</PrimaryButton>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
            </Box>
          )}

          {assignTab === 2 && (
            <HardwareTab
              flatRows={flatRows}
              assignments={assignments}
              projectKeyForStorage={projectKeyForStorage}
              labelStylesDark={labelStylesDark}
              selectStylesDark={selectStylesDark}
              setSnackbar={setSnackbar}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <PrimaryButton onClick={closeAssignDialog}>Close</PrimaryButton>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={2200}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        message={snackbar.msg}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{
          "& .MuiSnackbarContent-root": {
            backgroundColor: snackbar.color || "#4CAF50",
            color: "#fff",
            fontWeight: 500,
            padding: "10px 24px",
            minWidth: "auto",
            justifyContent: "center",
            textAlign: "center",
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
          },
        }}
      />
    </Box>
  );
}

/* ========== Small inline component ========== */
function StatPill({ label, value }: { label: string; value: number | string }) {
  return (
    <Box
      sx={{
        px: 1.5, py: 0.5, borderRadius: '4px',
        backgroundColor: 'rgba(255,255,255,0.10)',
        border: '1px solid rgba(255,255,255,0.18)',
        color: '#fff', fontSize: '0.8rem',
        display: 'inline-flex', alignItems: 'center', gap: 1,
      }}
    >
      <Box component="span" sx={{ opacity: 0.85 }}>{label}:</Box>
      <Box component="strong" sx={{ fontWeight: 700 }}>{value}</Box>
    </Box>
  );
}

/* ================== HARDWARE TAB ================== */
type HardwareTabProps = {
  flatRows: any[];
  assignments: AssignmentsMap;
  projectKeyForStorage: string;
  labelStylesDark: any;
  selectStylesDark: any;
  setSnackbar: (s: {open: boolean; msg: string; color?: string}) => void;
};

function HardwareTab({
  flatRows, assignments, projectKeyForStorage,
  labelStylesDark, selectStylesDark, setSnackbar
}: HardwareTabProps) {

  /* ---------- TYPES ---------- */
  type ModuleRow = {
    Brand:string; Family:string; Model:string; Description?:string;
    UnitPrice?:number; Currency?:string;
    AI_cap?:number; AO_cap?:number; DI_cap?:number; DO_cap?:number; UI_cap?:number;
    Supports_BACnet_IP?:string; Supports_BACnet_MSTP?:string; Supports_Modbus_TCP?:string; Supports_Modbus_RTU?:string; Supports_MBus?:string;
    GW_Proto?:string; GW_Device_Capacity?:number; GW_Ports?:number;
    PLC_Max_Slots?:number; PLC_Ethernet?:string; PLC_Redundant?:string; PLC_Webserver?:string;
    Width_mm?:number; Height_mm?:number; Depth_mm?:number; Weight_kg?:number; DIN_Modules?:number;
  };

  /* ---------- helpers ---------- */
  const isPLC = (m: ModuleRow) =>
    /plc|controller|cpu|station|automation|pxc7/i.test(`${m.Family} ${m.Model}`);
  const isGateway = (m: ModuleRow) =>
    /gateway/i.test(m.Family || "") || !!String(m.GW_Proto || "");
  const hasIOCap = (m: ModuleRow) =>
    num(m.AI_cap,0)+num(m.AO_cap,0)+num(m.DI_cap,0)+num(m.DO_cap,0)+num(m.UI_cap,0) > 0;
  const isIOModule = (m: ModuleRow) =>
    /io/i.test(m.Family || "") || hasIOCap(m);

  const num = (v:any, d=0) => {
    const n = typeof v === "number" ? v : parseFloat(String(v).replace(",", "."));
    return Number.isFinite(n) ? n : d;
  };
  const yes = (v:any) => /^(yes|y|true|1|evet)$/i.test(String(v).trim());

  // "any" hariçse evet/hayır kontrolü
  const matchesFeature = (val:any, want:'any'|'yes'|'no') =>
  want === 'any' || (want === 'yes' ? yes(val) : !yes(val));

  // Redundancy: "redundant" istenirse true bekler, "single" istenirse false
  const matchesRedundancy = (val:any, want:'any'|'single'|'redundant') =>
  want === 'any' || (want === 'redundant' ? yes(val) : !yes(val));

  const effWidth = (m:any) => { const w = num(m.Width_mm,0); if (w>0) return w; const din=num(m.DIN_Modules,0); return din>0? din*17.5 : 120; };

  const normProto = (s?: string) => String(s ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");
  const PROTO_ALIASES: Record<string,string[]> = {
    bacnetip:   ["bacnetip","bacnet-ip","bacnet ip"],
    bacnetmstp: ["bacnetmstp","bacnet-mstp","bacnet mstp","mstp"],
    modbustcp:  ["modbustcp","modbus-tcp","modbus tcp","modbustcpip"],
    modbusrtu:  ["modbusrtu","modbus-rtu","modbus rtu","rtu"],
    mbus:       ["mbus","m-bus","m bus"]
  };
  const matchesProto = (value: string, wanted: string) => {
    const key = normProto(wanted);
    const aliases = PROTO_ALIASES[key] || [key];
    const v = normProto(value);
    return aliases.includes(v);
  };

  function computeNeedsAndProtocols(pid: number, sparePct: number) {
    const rows = flatRows.filter(r => {
      const code = String(r.projectCode || "").trim();
      const asg = assignments[code];
      return asg?.panelId === pid;
    });
    const s = (k: "ai"|"ao"|"di"|"do"|"bacnetIp"|"bacnetMstp"|"modbusTcp"|"modbusRtu"|"mbus") =>
      rows.reduce((acc, r) => acc + num((r as any)[k], 0), 0);

    const needs = {
      AI: Math.ceil(s("ai") * (1 + sparePct / 100)),
      AO: Math.ceil(s("ao") * (1 + sparePct / 100)),
      DI: Math.ceil(s("di") * (1 + sparePct / 100)),
      DO: Math.ceil(s("do") * (1 + sparePct / 100)),
    };
    const proto = {
      bacnetIp:   s("bacnetIp"),
      bacnetMstp: s("bacnetMstp"),
      modbusTcp:  s("modbusTcp"),
      modbusRtu:  s("modbusRtu"),
      mbus:       s("mbus"),
    };
    return { needs, proto };
  }

  function buildSiemensByRules(
    ai: number, ao: number, di: number, doCount: number,
    list: ModuleRow[]
  ) {
    // ——— Model bulucu
    const byModel = (name: string) =>
      list.find(m => String(m.Model || "").trim().toUpperCase() === name.toUpperCase());

    const M8U   = byModel("TXM1.8U");
    const M16D  = byModel("TXM1.16D");
    const M8D   = byModel("TXM1.8D");
    const M6R   = byModel("TXM1.6R");
    const M4D3R = byModel("TXM1.4D3R");

    const P_S   = byModel("PXC7.E400S");
    const P_M   = byModel("PXC7.E400M");
    const P_L   = byModel("PXC7.E400L");

    const need = (x: ModuleRow | undefined, name: string) => {
      if (!x) throw new Error(`Module List içinde eksik model: ${name}`);
      return x;
    };

    // ——— Adet hesapları
    const q8U  = Math.ceil((ai + ao) / 8);

    const di16 = Math.floor(di / 16);
    const diRem = di % 16;
    let q16D = di16;
    let q8D  = 0;
    if (diRem > 0) {
      if (diRem >= 8) q16D += 1;
      else q8D = 1;
    }

    let q6R = Math.ceil(doCount / 6);
    const doRem = doCount % 6;

    // ——— Opsiyonel TXM1.4D3R kombinasyonu
    let q4D3R = 0;
    if (M4D3R && diRem > 0 && doRem > 0 && diRem <= 4 && doRem <= 3 && q8D > 0 && q6R > 0) {
      q8D  -= 1;
      q6R  -= 1;
      q4D3R = 1;
    }

    const modulesCount = q8U + q16D + q8D + q6R + q4D3R;
    if (modulesCount > 48) {
      throw new Error(`Modül adedi ${modulesCount} > 48 (PLC slot limiti)`);
    }

    const totalPoints = ai + ao + di + doCount;

    // ——— PLC seçimi (point sayısına göre min model)
    let plc: ModuleRow;
    if (totalPoints > 200)      plc = need(P_L, "PXC7.E400L");
    else if (totalPoints > 100) plc = need(P_M, "PXC7.E400M");
    else                        plc = need(P_S, "PXC7.E400S");

    // ——— Paket
    const modules: Array<{ item: ModuleRow; qty: number }> = [];
    if (q8U  > 0) modules.push({ item: need(M8U,  "TXM1.8U"),  qty: q8U  });
    if (q16D > 0) modules.push({ item: need(M16D, "TXM1.16D"), qty: q16D });
    if (q8D  > 0) modules.push({ item: need(M8D,  "TXM1.8D"),  qty: q8D  });
    if (q6R  > 0) modules.push({ item: need(M6R,  "TXM1.6R"),  qty: q6R  });
    if (q4D3R > 0 && M4D3R) modules.push({ item: M4D3R, qty: q4D3R });

    // ——— Özet
    const effWidthLocal = (m:any) => {
      const w = num(m.Width_mm,0);
      if (w>0) return w;
      const din = num(m.DIN_Modules,0);
      return din>0? din*17.5 : 120;
    };

    let cost = num(plc.UnitPrice,0);
    let width = effWidthLocal(plc);
    for (const m of modules) {
      cost  += num(m.item.UnitPrice,0) * m.qty;
      width += effWidthLocal(m.item) * m.qty;
    }

    return {
    plc,
    modules,
    gateways: [] as Array<{ item: ModuleRow; qty: number }>,
    addressKeys: [] as Array<{ item: ModuleRow; qty: number }>,
    powerSupplies: [] as Array<{ item: ModuleRow; qty: number }>, // NEW
    summary: { cost, width, slotsUsed: modulesCount, slotsLimit: 48 }
    };
    }

  const rowsByPanel = React.useMemo(() => {
    const map = new Map<number, { panelName: string; io: {AI:number;AO:number;DI:number;DO:number} }>();
    const n = (x:any)=> Number.isFinite(x)? x : num(x,0);
    for (const r of flatRows) {
      const code = String(r.projectCode||"").trim();
      if (!code) continue;
      const asg = assignments[code];
      if (!asg) continue;
      if (!map.has(asg.panelId)) map.set(asg.panelId, { panelName: asg.panelName, io:{AI:0,AO:0,DI:0,DO:0} });
      const b = map.get(asg.panelId)!;
      b.io.AI += n(r.ai); b.io.AO += n(r.ao); b.io.DI += n(r.di); b.io.DO += n(r.do);
    }
    return map;
  }, [flatRows, assignments]);

  const [moduleList, setModuleList] = React.useState<ModuleRow[]>([]);
  const [moduleBrand, setModuleBrand] = React.useState<string>("");
  const distinctModuleBrands = React.useMemo(() => {
    const uniq = new Set<string>();
    for (const m of moduleList) { const b = (m.Brand || "").trim(); if (b) uniq.add(b); }
    return Array.from(uniq).sort((a, b) => a.localeCompare(b, "tr"));
  }, [moduleList]);
  React.useEffect(() => {
    if (distinctModuleBrands.length === 1) setModuleBrand(distinctModuleBrands[0]);
    else if (distinctModuleBrands.length === 0) setModuleBrand("");
  }, [distinctModuleBrands]);

  const MODEL_BLOCKLIST = new Set(["TXM1.8T"]);
  const workingList = React.useMemo<ModuleRow[]>(
  () => {
    const base = moduleBrand
      ? moduleList.filter((m) => normalize(m.Brand || "") === normalize(moduleBrand))
      : moduleList;

    // TXM1.8T global blok: listeye hiç girmesin
    return base.filter(m => !MODEL_BLOCKLIST.has(String(m.Model || "").trim().toUpperCase()));
  },
  [moduleList, moduleBrand]
);
  const brandChoiceRequired: boolean = distinctModuleBrands.length > 1 && !moduleBrand;

  const loadXLSX = React.useCallback(async()=>{ const mod:any = await import("xlsx"); return mod?.default ?? mod; },[]);
  const moduleExcelRef = React.useRef<HTMLInputElement>(null);
  const onModuleExcel = async (file: File) => {
    try {
      const XLSX = await loadXLSX();
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array", cellDates: true, dense: true });
      const ws = wb.Sheets["Data"] || wb.Sheets[wb.SheetNames[0]];
      if (!ws) throw new Error("No 'Data' sheet");
      const rows = XLSX.utils.sheet_to_json(ws, { defval: "" }) as any[];
      const list: ModuleRow[] = rows.map(r => ({
        Brand: r["Brand"]??"", Family: r["Family"]??"", Model: r["Model"]??"", Description: r["Description"]??"",
        UnitPrice: num(r["UnitPrice"],0), Currency: r["Currency"]??"",
        AI_cap: num(r["AI_cap"] ?? r["AI"] ?? r["AI Channels"] ?? r["AI Ch"] ?? 0, 0),
        AO_cap: num(r["AO_cap"] ?? r["AO"] ?? r["AO Channels"] ?? r["AO Ch"] ?? 0, 0),
        DI_cap: num(r["DI_cap"] ?? r["DI"] ?? r["DI Channels"] ?? r["DI Ch"] ?? 0, 0),
        DO_cap: num(r["DO_cap"] ?? r["DO"] ?? r["DO Channels"] ?? r["DO Ch"] ?? 0, 0),
        UI_cap: num(r["UI_cap"] ?? r["UI"] ?? r["Universal"] ?? r["Universal IO"] ?? r["Universal_IO"] ?? r["U/I"] ?? 0, 0),
        Supports_BACnet_IP: r["Supports_BACnet_IP"]??"", Supports_BACnet_MSTP: r["Supports_BACnet_MSTP"]??"",
        Supports_Modbus_TCP: r["Supports_Modbus_TCP"]??"", Supports_Modbus_RTU: r["Supports_Modbus_RTU"]??"", Supports_MBus: r["Supports_MBus"]??"",
        GW_Proto: r["GW_Proto"]??"", GW_Device_Capacity: num(r["GW_Device_Capacity"],0), GW_Ports: num(r["GW_Ports"],0),
        PLC_Max_Slots: num(r["PLC_Max_Slots"], NaN), PLC_Ethernet:  r["PLC_Ethernet"]  ?? r["Ethernet"]  ?? r["PLC Ethernet"] ?? "", PLC_Redundant: r["PLC_Redundant"] ?? r["Redundant"] ?? r["Redundancy"] ?? "", PLC_Webserver: r["PLC_Webserver"] ?? r["Webserver"] ?? r["PLC Webserver"] ?? "",
        Width_mm: num(r["Width_mm"],0), Height_mm: num(r["Height_mm"],0), Depth_mm: num(r["Depth_mm"],0), Weight_kg: num(r["Weight_kg"],0), DIN_Modules: num(r["DIN_Modules"],0),
      }));
      setModuleList(list);
      
      setSnackbar({ open:true, msg:`Module List loaded (${list.length})`, color:"#4CAF50" });
    } catch(e) {
      console.error("[Module Excel Import] Error:", e);
      setSnackbar({ open:true, msg:"Could not read Module List", color:"#E53935" });
    }
  };

  /* ---------- deterministic Siemens rules (gateways disabled) ---------- */
    type HardwarePick = {
    plc?: ModuleRow;
    modules: Array<{ item: ModuleRow; qty: number }>;
    gateways: Array<{ item: ModuleRow; qty: number }>;
    addressKeys: Array<{ item: ModuleRow; qty: number }>;
    powerSupplies: Array<{ item: ModuleRow; qty: number }>; // NEW
    summary: { cost:number; width:number; slotsUsed:number; slotsLimit?:number };
    };

  /* ---------- UI state ---------- */
  const [panelId, setPanelId] = React.useState<number | ''>('');
  const [spare, setSpare] = React.useState<number>(10);

  // --- NEW: PLC feature filters ---
  const [plcRedundancy, setPlcRedundancy] = React.useState<'any'|'single'|'redundant'>('any');
  const [plcWebserver,  setPlcWebserver]  = React.useState<'any'|'yes'|'no'>('any');
  const [plcEthernet,   setPlcEthernet]   = React.useState<'any'|'yes'|'no'>('any');


  // Calculate öncesi zorunlu alan kontrolü
  const [calcTouched, setCalcTouched] = React.useState(false);

  const isComplete = React.useMemo(() => (
  panelId !== '' &&
  (moduleBrand?.trim() ?? '') !== '' &&
  plcRedundancy !== 'any' &&
  plcWebserver  !== 'any' &&
  plcEthernet   !== 'any' &&
  Number.isFinite(spare)
  ), [panelId, moduleBrand, plcRedundancy, plcWebserver, plcEthernet, spare]);

  const calculateGuarded = () => {
  setCalcTouched(true);
  if (!isComplete) {
    setSnackbar({
      open: true,
      msg: "Please fill all fields: Panel, Brand, Redundancy, Webserver, Ethernet.",
      color: "#E53935"
    });
    return;
  }
  calculate();
  };

  // UI Allocation selection kaldırıldı; dahili politika:
  const uiPolicy: "AI_FIRST" | "DI_FIRST" = "AI_FIRST";

  /* ---------- generic optimizer (fallback for non-Siemens) ---------- */
  function pickBest(
    plcs: ModuleRow[],
    needs: { AI:number; AO:number; DI:number; DO:number },
    proto: { bacnetIp:number; bacnetMstp:number; modbusTcp:number; modbusRtu:number; mbus:number },
    workingList: ModuleRow[]
  ): HardwarePick | null {
    const ioMods: ModuleRow[] = workingList.filter(isIOModule);
    if (plcs.length === 0) return null;

    let best: HardwarePick | null = null;

    for (const plc of plcs) {
      const gw: Array<{ item: ModuleRow; qty: number }> = [];

      let remAI = Math.max(0, needs.AI - num(plc.AI_cap,0));
      let remAO = Math.max(0, needs.AO - num(plc.AO_cap,0));
      let remDI = Math.max(0, needs.DI - num(plc.DI_cap,0));
      let remDO = Math.max(0, needs.DO - num(plc.DO_cap,0));
      let plcUI = num(plc.UI_cap,0);

      const alloc = (want: "AI"|"DI"|"AO") => {
        if (plcUI <= 0) return;
        if (want === "AI") { const x = Math.min(plcUI, remAI); remAI -= x; plcUI -= x; return; }
        if (want === "DI") { const x = Math.min(plcUI, remDI); remDI -= x; plcUI -= x; return; }
        if (want === "AO") { const x = Math.min(plcUI, remAO); remAO -= x; plcUI -= x; return; }
      };
      if (uiPolicy === "AI_FIRST") { alloc("AI"); alloc("DI"); alloc("AO"); }
      else                         { alloc("DI"); alloc("AI"); alloc("AO"); }

      const modules: Array<{ item: ModuleRow; qty: number }> = [];
      let slotsUsed = 0;
      const maxSlotsNum = num(plc.PLC_Max_Slots, NaN);
      const slotsLimit = Number.isFinite(maxSlotsNum) && maxSlotsNum > 0 ? maxSlotsNum : undefined;

      const choose = (kind: "AO"|"DO"|"AI"|"DI") => {
        const unitRemaining = () => ({ AO: remAO, DO: remDO, AI: remAI, DI: remDI }[kind]);
        let guard = 0;
        while (unitRemaining() > 0 && guard < 100) {
          const diModsExist = ioMods.some(m => num(m.DI_cap,0) > 0);
          const cands = ioMods.filter(m => {
            const contributes =
              (num(m.AO_cap,0)>0 && remAO>0) ||
              (num(m.DO_cap,0)>0 && remDO>0) ||
              (num(m.AI_cap,0)>0 && remAI>0) ||
              (num(m.DI_cap,0)>0 && remDI>0) ||
              (num(m.UI_cap,0)>0 && (remAI>0 || remDI>0 || remAO>0));
            if (!contributes) return false;
            if (kind === "DI" && diModsExist) return num(m.DI_cap,0) > 0;
            return true;
          });

          if (cands.length === 0) break;

          const prefFactor = (m: ModuleRow, kind: "AO"|"DO"|"AI"|"DI", a: { aAI:number; aDI:number; aAO:number; aDO:number }, rem: { AI:number; DI:number; AO:number; DO:number }) => {
            let f = 1;
            const model = String(m.Model||"");
            const is8U   = /TXM1\.8U/i.test(model);
            const is8D   = /TXM1\.8D/i.test(model);
            const is16D  = /TXM1\.16D/i.test(model);
            const is6R   = /TXM1\.6R/i.test(model);
            const is4D3R = /TXM1\.4D3R/i.test(model);

            if ((kind==="AI" || kind==="AO") && is8U) f *= 0.85;
            if (kind==="DO" && is6R) f *= 0.85;
            if (kind==="DI") {
              if (rem.DI >= 9 && is16D) f *= 0.80;
              else if (is8D) f *= 0.85;
            }
            // 4D3R yalnızca küçük kalanlarda teşvik edilsin; erken kullanım cezalı
            if (is4D3R) {
              if (rem.DI > 0 && rem.DI <= 4 && rem.DO > 0 && rem.DO <= 3) f *= 0.70;
              else f *= 1.15;
            }
            if (is8U && ((a.aAI>0 && a.aDI>0) || (a.aAI>0 && a.aAO>0))) f *= 0.90;
            return f;
          };

          const evalCand = (m: ModuleRow) => {
            let aAI = Math.min(remAI, num(m.AI_cap,0));
            let aDI = Math.min(remDI, num(m.DI_cap,0));
            let aAO = Math.min(remAO, num(m.AO_cap,0));
            let aDO = Math.min(remDO, num(m.DO_cap,0));

            let ui = num(m.UI_cap,0);
            const applyUI = (want: "AI"|"DI"|"AO") => {
              if (ui <= 0) return;
              if (want === "AI") { const need = Math.max(0, remAI - aAI); const x = Math.min(need, ui); aAI += x; ui -= x; return; }
              if (want === "DI") { const need = Math.max(0, remDI - aDI); const x = Math.min(need, ui); aDI += x; ui -= x; return; }
              if (want === "AO") { const need = Math.max(0, remAO - aAO); const x = Math.min(need, ui); aAO += x; ui -= x; return; }
            };
            if (ui > 0) {
              if (uiPolicy === "AI_FIRST") { applyUI("AI"); applyUI("DI"); applyUI("AO"); }
              else                          { applyUI("DI"); applyUI("AI"); applyUI("AO"); }
            }

            const primary = kind==="AO"?aAO:kind==="DO"?aDO:kind==="AI"?aAI:aDI;
            const others  = (aAI + aDI + aAO + aDO) - primary;
            const effUnits = primary + 0.6*others;

            const price = num(m.UnitPrice, 9999);
            const width = effWidth(m);

            const factor = prefFactor(m, kind, {aAI, aDI, aAO, aDO}, {AI: remAI, DI: remDI, AO: remAO, DO: remDO});

            const score = (price / Math.max(1, effUnits)) * factor + width * 1e-6;

            return { m, aAI, aDI, aAO, aDO, score };
          };

          const evaluated = cands.map(evalCand).filter(e => (e.aAI+e.aDI+e.aAO+e.aDO) > 0);
          if (evaluated.length === 0) break;
          evaluated.sort((a,b)=> a.score - b.score);
          const pickM = evaluated[0];

          modules.push({ item: pickM.m, qty: 1 });

          remAI = Math.max(0, remAI - pickM.aAI);
          remAO = Math.max(0, remAO - pickM.aAO);
          remDI = Math.max(0, remDI - pickM.aDI);
          remDO = Math.max(0, remDO - pickM.aDO);

          slotsUsed += 1;
          if (slotsLimit && slotsUsed > slotsLimit) break;
          if (modules.length > 200) break;
          guard++;
        }
      };

      choose("AO"); choose("DO"); choose("AI"); choose("DI");
      if (remAI>0 || remAO>0 || remDI>0 || remDO>0) continue;
      if (slotsLimit && slotsUsed > slotsLimit) continue;

      let cost = num(plc.UnitPrice,0), width = effWidth(plc);
      for (const m of modules) { cost += num(m.item.UnitPrice,0)*m.qty; width += effWidth(m.item)*m.qty; }
      for (const g of gw)     { cost += num(g.item.UnitPrice,0)*g.qty;   width += effWidth(g.item)*g.qty; }

      const pick: HardwarePick = {  plc,  modules,  gateways: gw,  addressKeys: [],  powerSupplies: [],   summary: { cost, width, slotsUsed, slotsLimit }};
      const better = (a:HardwarePick|null, b:HardwarePick) => {
        if (!a) return true;
        if (b.summary.cost !== a.summary.cost) return b.summary.cost < a.summary.cost;
        return b.summary.width < a.summary.width;
      };
      if (better(best, pick)) best = pick;
    }
    return best;
  }

  // Materials summary for panel (same key as PoolPage)
  type CatalogItemLocal = CatalogItem;
  const MATERIALS_KEY_HW = "materials:selections:v1";
  const [materialsMap, setMaterialsMap] = React.useState<RowSelectionMap>({});
  React.useEffect(() => { try { const raw = localStorage.getItem(MATERIALS_KEY_HW); setMaterialsMap(raw ? JSON.parse(raw) : {}); } catch { setMaterialsMap({}); } }, []);
  const materialsForPanelAll = React.useCallback((pid: number): CatalogItemLocal[] => {
    const acc: CatalogItemLocal[] = [];
    flatRows.forEach((row, idx) => {
      const code = String(row.projectCode || "").trim();
      const asg = assignments[code];
      if (asg?.panelId === pid) {
        const key = buildRowKey(row, idx);
        const arr = materialsMap[key] || [];
        acc.push(...arr);
      }
    });
    return acc;
  }, [flatRows, assignments, materialsMap]);
  const materialLabel = (it: CatalogItemLocal) =>
    it.name || it.code || it.model || [it.brand, it.family].filter(Boolean).join(" ") || "item";
  const DESC_KEYS = ["description", "açıklama", "desc", "details", "detay"];
  const materialDesc = (it: CatalogItemLocal) => {
    const raw = (it.raw || {}) as Record<string, any>;
    const lower = Object.fromEntries(Object.keys(raw).map(k => [k.toLowerCase(), (raw as any)[k]]));
    for (const k of DESC_KEYS) if (lower[k]) return String(lower[k]).trim();
    const parts = [it.brand, it.family || it.category, it.measuring, it.signal, it.mounting]
      .filter(Boolean).map(String);
    return parts.join(", ");
  };
  const aggregateMaterialsForPanel = React.useCallback((pid: number) => {
    const all = materialsForPanelAll(pid);
    const map = new Map<string, { item: CatalogItemLocal; qty: number }>();
    for (const it of all) {
      const k = it.id || `${it.brand}|${it.family}|${it.model}|${it.code}|${it.name}`;
      if (!map.has(k)) map.set(k, { item: it, qty: 0 });
      map.get(k)!.qty += 1;
    }
    return Array.from(map.values())
      .map(({ item, qty }) => ({ qty, label: materialLabel(item), desc: materialDesc(item), raw: item.raw || {} }))
      .sort((a, b) => a.label.localeCompare(b.label, "tr"));
  }, [materialsForPanelAll]);

  // === Material pricing helpers ===
  const materialPrice = (it: CatalogItemLocal) => {
    const raw = (it.raw || {}) as Record<string, any>;
    const lower: Record<string, any> = {};
    Object.keys(raw).forEach(k => { lower[k.toLowerCase()] = raw[k]; });
    const v = lower["unitprice"] ?? lower["unit_price"] ?? lower["price"] ?? lower["fiyat"];
    const n = typeof v === "number" ? v : parseFloat(String(v).replace(",", "."));
    return Number.isFinite(n) ? n : 0;
  };
  const materialCurrency = (it: CatalogItemLocal) => {
    const raw = (it.raw || {}) as Record<string, any>;
    const lower: Record<string, any> = {};
    Object.keys(raw).forEach(k => { lower[k.toLowerCase()] = raw[k]; });
    const c = lower["currency"] ?? lower["para birimi"] ?? "EUR";
    return String(c || "EUR").toUpperCase();
  };
  const materialsTotalsByCurrency = (pid: number) => {
    const list = materialsForPanelAll(pid);
    const map = new Map<string, number>();
    for (const it of list) {
      const cur = materialCurrency(it);
      const price = materialPrice(it);
      map.set(cur, (map.get(cur) || 0) + (Number.isFinite(price) ? price : 0));
    }
    return map;
  };

  /* ---------- actions ---------- */
  const [hardware, setHardware] = React.useState<Record<number, HardwarePick>>({});
  const HARDWARE_KEY = `panelHardware:${projectKeyForStorage}`;
  React.useEffect(()=>{ try{ const raw=localStorage.getItem(HARDWARE_KEY); setHardware(raw? JSON.parse(raw): {});}catch{ setHardware({}); } },[HARDWARE_KEY]);
  const persistHardware = (next:Record<number,HardwarePick>) => { try{ localStorage.setItem(HARDWARE_KEY, JSON.stringify(next)); }catch{} };

  const calculate = (): void => {
    if (panelId === '') return;
    if (moduleList.length === 0) {
      setSnackbar({ open:true, msg:"Load Module List first", color:"#E53935" });
      return;
    }

    try {
      const { needs, proto } = computeNeedsAndProtocols(panelId as number, spare);
      const list = (workingList.length > 0 ? workingList : moduleList)
      .filter(m => !MODEL_BLOCKLIST.has(String(m.Model || "").trim().toUpperCase()));

      // Siemens model seti mevcut mu?
      const siemensModels = ["TXM1.8U","TXM1.16D","TXM1.8D","TXM1.6R","TXM1.4D3R","PXC7.E400S","PXC7.E400M","PXC7.E400L"];
      const siemensAvailable = siemensModels
      .map(s => s.toUpperCase())
      .every(m =>
      list.some(x => String(x.Model || "").trim().toUpperCase() === m)
      );

      // PLC adaylarını marka + özellik filtreleriyle süz
      const plcAll = list.filter(isPLC);
      const plcFiltered = plcAll.filter(p =>
        matchesRedundancy(p.PLC_Redundant, plcRedundancy) &&
        matchesFeature(p.PLC_Webserver, plcWebserver) &&
        matchesFeature(p.PLC_Ethernet, plcEthernet)
      );

      if (plcFiltered.length === 0) {
        setSnackbar({ open:true, msg:"No PLC matches the selected features (Redundancy/Webserver/Ethernet).", color:"#E53935" });
        return;
      }

      // Siemens + filtreler "any" ise deterministik; değilse generic
      const wantGeneric =
        plcRedundancy !== 'any' || plcWebserver !== 'any' || plcEthernet !== 'any' ||
        (!siemensAvailable && normalize(moduleBrand) !== 'siemens');

      // Total points → Siemens CPU minimum model zorlaması (generic yolda)
      const totalPoints = needs.AI + needs.AO + needs.DI + needs.DO;
      let plcCandidates = plcFiltered;
      if (normalize(moduleBrand) === 'siemens' || siemensAvailable) {
        const plcOrder = ["PXC7.E400S","PXC7.E400M","PXC7.E400L"];
        const minModelByPoints = totalPoints > 200 ? "PXC7.E400L" : totalPoints > 100 ? "PXC7.E400M" : "PXC7.E400S";
        const minIdx = plcOrder.indexOf(minModelByPoints);
        plcCandidates = plcFiltered.filter(p => {
          const idx = plcOrder.indexOf(String(p.Model||"").trim().toUpperCase());
          return idx >= 0 && idx >= minIdx; // sadece Siemens ve min modelden büyük/eşit
        });
        if (wantGeneric && plcCandidates.length === 0) {
          setSnackbar({ open:true, msg:"No Siemens PLC meets the minimum size for total points.", color:"#E53935" });
          return;
        }
      }

      let pick: HardwarePick | null;
      if (wantGeneric) {
        pick = pickBest(plcCandidates, needs, proto, list);
        if (!pick) {
          setSnackbar({ open:true, msg:"No feasible hardware pack with current filters.", color:"#E53935" });
          return;
        }
      } else {
        pick = buildSiemensByRules(needs.AI, needs.AO, needs.DI, needs.DO, list);
      }

// ==== Adress Key (Siemens TXM1.xxx sayısına göre) ====
if (pick) {
  const isSiemens = (m: ModuleRow) => normalize(m.Brand || "") === "siemens";
  const isTXM1    = (model?: string) => /^TXM1\./i.test(String(model || "").trim());

  // Seçili pack içindeki Siemens TXM1.xxx modül sayısı (qty toplamı)
  const txmCount = pick.modules.reduce((acc, x) => {
    const yes = isSiemens(x.item) && isTXM1(x.item.Model);
    return acc + (yes ? (typeof x.qty === "number" ? x.qty : 1) : 0);
  }, 0);

  // Excel'den bu modellerin yüklü olduğunu varsayıyoruz
  const byModel = (name: string) =>
    moduleList.find(m => String(m.Model || "").trim().toUpperCase() === name.toUpperCase());

  const AK12 = byModel("TXA1.K12");
  const AK24 = byModel("TXA1.K24");
  const AK48 = byModel("TXA1.K-48");

  const ak: Array<{ item: ModuleRow; qty: number }> = [];

  if (txmCount > 0 && isFinite(txmCount)) {
    if (txmCount <= 12) {
      if (AK12) ak.push({ item: AK12, qty: 1 });
    } else if (txmCount >= 13 && txmCount <= 24) {
      if (AK24) ak.push({ item: AK24, qty: 1 });
    } else if (txmCount >= 25 && txmCount <= 48) {
      if (AK24) ak.push({ item: AK24, qty: 1 });
      if (AK48) ak.push({ item: AK48, qty: 1 });
    }
    // >48 durumu belirtilmedi; Siemens kuralında zaten 48 slot limiti var.
  }

  // Maliyet/genişlik toplamlarına yansıt
  for (const k of ak) {
    pick.summary.cost  += num(k.item.UnitPrice, 0) * k.qty;
    pick.summary.width += effWidth(k.item) * k.qty;
  }
  pick.addressKeys = ak;
  // ==== Power Supplys (Siemens TXM1.xxx sayısına göre) ====
  // Yalnızca Brand 'Siemens' seçiliyse çalışır
  if (normalize(moduleBrand) === 'siemens') {
    const PSU = byModel("TXS1.12F10");
    let psuQty = 0;
    if (txmCount > 0) {
      if (txmCount <= 16) psuQty = 1;
      else if (txmCount <= 34) psuQty = 2;
      else psuQty = 3; // 35–48
    }
    if (PSU && psuQty > 0) {
      pick.summary.cost  += num(PSU.UnitPrice, 0) * psuQty;
      pick.summary.width += effWidth(PSU) * psuQty;
      pick.powerSupplies = [{ item: PSU, qty: psuQty }];
    }
  }
}


      const next = { ...hardware, [panelId as number]: pick };
      setHardware(next);
      if (pick.modules.some(x => MODEL_BLOCKLIST.has(String(x.item.Model || "").trim().toUpperCase()))) {
      setSnackbar({ open: true, msg: "Blocked model (TXM1.8T) detected in result. Check Module List.", color: "#E53935" });
      return;
      }
      setSnackbar({ open: true, msg: "Hardware calculated", color: "#4CAF50" });
    } catch (e:any) {
      setSnackbar({ open: true, msg: String(e.message || e), color: "#E53935" });
    }
  };

  const save = (): void => {
    if (panelId==='') return;
    if (!hardware[panelId as number]) { setSnackbar({open:true, msg:"Nothing to save. Calculate first.", color:"#E53935"}); return; }
    persistHardware(hardware);
    setSnackbar({open:true, msg:"Hardware saved to panel", color:"#4CAF50"});
  };


  /* ============ ASSIGNED PANELS POPUP: state + helpers ============ */
    const [assignedOpen, setAssignedOpen] = React.useState(false);

  // Başlığa konacak proje adı (Assign to Panel başlığıyla aynı mantık)
    const projectName = React.useMemo(
    () => (projectKeyForStorage && projectKeyForStorage !== "__all__" ? projectKeyForStorage : ""),
    [projectKeyForStorage]
    );

  /** panels:* kayıtlarından id -> {name, located} indeksini hazırla */
    const panelsIndex = React.useMemo(() => {
    const map = new Map<number, { name: string; located?: string }>();
    const keys = Object.keys(localStorage).filter(k => k.startsWith("panels:"));
    for (const k of keys) {
      try {
        const arr = JSON.parse(localStorage.getItem(k) || "[]");
        if (Array.isArray(arr)) {
          for (const rec of arr) {
            if (rec?.id) {
              map.set(rec.id, {
                name: rec.panel?.name || `#${rec.id}`,
                located: rec.panel?.located || ""
              });
            }
          }
        }
      } catch {}
    }
    return map;
  }, []);

  /** localStorage'daki kaydedilmiş hardware → tabloya uygun liste */
    const savedList = React.useMemo(() => {
      return Object.entries(hardware).map(([pid, pick]) => {
      const id = Number(pid);
      const info = panelsIndex.get(id) || { name: `#${id}`, located: "" };

      const controller = pick?.plc
      ? `${pick.plc.Brand || ""} ${pick.plc.Model || ""}`.trim()
      : "—";

    // Total Cost (EUR) = hardware cost + (varsa) materyaller EUR toplamı
      const matsTotals = materialsTotalsByCurrency(id);
      const matsEUR = matsTotals.get("EUR") || 0;
      const hwEUR = Number.isFinite(pick?.summary?.cost) ? pick.summary.cost : 0;
      const totalEUR = hwEUR + matsEUR;

    return {
      id,
      panelName: info.name,
      located: info.located || "—",
      controller,
      totalEUR
    };
  }).sort((a, b) => a.panelName.localeCompare(b.panelName, "tr"));
}, [hardware, panelsIndex]);

/** bir panelin kaydını sil (localStorage + state) */
const handleDeleteSaved = (id: number) => {
  const next = { ...hardware };
  delete next[id];
  setHardware(next);
  persistHardware(next);
  setSnackbar({ open: true, msg: "Saved hardware removed for panel", color: "#4CAF50" });
};


  /* ---------- UI ---------- */
  const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Stack spacing={0.5} sx={{ mt: 1 }}>
    <Typography
      variant="subtitle1"
      component="div"            // <h*> yerine <div>: default margin yok
      sx={{ fontWeight: 700, lineHeight: 1.2, mb: 0 }}
    >
      {title}
    </Typography>
    {children}
  </Stack>
);
  return (
    <Box>
      {/* Üst satır: panel seç + Brand + Spare% + upload */}
      <Stack   direction={{ xs: "column", md: "row" }}  spacing={2}  alignItems="flex-end"  sx={{ mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <FormControl size="small" required error={calcTouched && panelId === ''} sx={{ minWidth: 240 }}>
          <InputLabel sx={labelStylesDark}>Select Panel</InputLabel>
          <Select
            label="Select Panel"
            value={panelId === '' ? '' : String(panelId)}
            onChange={(e)=> setPanelId(e.target.value ? Number(e.target.value) : '')}
            sx={selectStylesDark}
            MenuProps={{ PaperProps: { sx: { bgcolor: '#1e1e1e', color: '#fff' } } }}
          >
            <MenuItem value="">(Choose)</MenuItem>
            {Array.from(rowsByPanel.entries()).map(([pid, v]) => (
              <MenuItem key={pid} value={String(pid)}>{v.panelName}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" required error={calcTouched && (moduleBrand?.trim() === '')} sx={{ minWidth: 220 }}>
          <InputLabel sx={labelStylesDark}>Brand</InputLabel>
          <Select
            label="Brand"
            value={moduleBrand}
            onChange={(e) => setModuleBrand(String(e.target.value))}
            sx={selectStylesDark}
            MenuProps={{ PaperProps: { sx: { bgcolor: '#1e1e1e', color: '#fff' } } }}
          >
            <MenuItem value="">(All)</MenuItem>
            {distinctModuleBrands.map((b) => (<MenuItem key={b} value={b}>{b}</MenuItem>))}
          </Select>
        </FormControl>

        {/* NEW: Redundancy */}
        <FormControl size="small" required error={calcTouched && plcRedundancy === 'any'} sx={{ minWidth: 180 }}>
        <InputLabel sx={labelStylesDark}>Redundancy</InputLabel>
        <Select
        label="Redundancy"
        value={plcRedundancy}
        onChange={(e) => setPlcRedundancy(e.target.value as any)}
        sx={selectStylesDark}
        MenuProps={{ PaperProps: { sx: { bgcolor: '#1e1e1e', color: '#fff' } } }}
        >
    <MenuItem value="any">(Any)</MenuItem>
    <MenuItem value="single">Single</MenuItem>
    <MenuItem value="redundant">Redundant</MenuItem>
  </Select>
</FormControl>

{/* NEW: Webserver */}
<FormControl size="small" required error={calcTouched && plcEthernet === 'any'} sx={{ minWidth: 160 }}>
  <InputLabel sx={labelStylesDark}>Webserver</InputLabel>
  <Select
    label="Webserver"
    value={plcWebserver}
    onChange={(e) => setPlcWebserver(e.target.value as any)}
    sx={selectStylesDark}
    MenuProps={{ PaperProps: { sx: { bgcolor: '#1e1e1e', color: '#fff' } } }}
  >
    <MenuItem value="any">(Any)</MenuItem>
    <MenuItem value="yes">Required</MenuItem>
    <MenuItem value="no">No Webserver</MenuItem>
  </Select>
</FormControl>

{/* NEW: Ethernet */}
<FormControl size="small" required error={calcTouched && plcWebserver === 'any'} sx={{ minWidth: 160 }}>
  <InputLabel sx={labelStylesDark}>Ethernet</InputLabel>
  <Select
    label="Ethernet"
    value={plcEthernet}
    onChange={(e) => setPlcEthernet(e.target.value as any)}
    sx={selectStylesDark}
    MenuProps={{ PaperProps: { sx: { bgcolor: '#1e1e1e', color: '#fff' } } }}
  >
    <MenuItem value="any">(Any)</MenuItem>
    <MenuItem value="yes">Required</MenuItem>
    <MenuItem value="no">No Ethernet</MenuItem>
  </Select>
</FormControl>


        {/* Spare % üst satırda */}
        <TextField
          label="Spare %"
          required
          type="number"
          value={spare}
          onChange={(e)=> {
            const n = Math.round(Number(e.target.value));
            const clamped = Math.min(50, Math.max(0, isFinite(n) ? n : 0));
            setSpare(clamped);
          }}
          size="small"
          sx={{ minWidth: 140, mt: 1, ml: { xs: 0, md: -2 }, ...textFieldStylesDark }}
          InputProps={{ inputProps:{ step:1, min:0, max:50 } }}
          InputLabelProps={{ sx: { color: '#BDBDBD', '&.Mui-focused': { color: '#E0E0E0' } } }}
        />

        <Box sx={{ flexGrow: 1 }} />

        
      </Stack>

      {/* Butonlar */}
      <Box sx={{ mb: 2 }}>
  {/* hidden input burada da durabilir */}
  <input
    ref={moduleExcelRef}
    type="file"
    hidden
    accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
    onChange={(e)=>{ const f=e.target.files?.[0]; if(f) onModuleExcel(f); (e.target as HTMLInputElement).value=""; }}
  />
  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap:'wrap' }}>
    <Box sx={{ flexGrow:1 }} />
    <ModernButton onClick={()=>moduleExcelRef.current?.click()}>Upload Excel</ModernButton>
    <PrimaryButton onClick={() => setAssignedOpen(true)}>Assigned Panels</PrimaryButton>
    <PrimaryButton onClick={calculateGuarded} disabled={!isComplete}>Calculate</PrimaryButton>
    <PrimaryButton onClick={save}>Save</PrimaryButton>
  </Stack>
  </Box>
      {/* Sonuç */}
      <Box sx={{ border:"1px solid #eee", borderRadius:1, overflow:"hidden" }}>
        {panelId!=='' && hardware[panelId as number] ? (
          <Box sx={{ p:2, bgcolor:"#fff", color:"#333" }}>
            {(() => {
              const pick = hardware[panelId as number]!;
              const pi = rowsByPanel.get(panelId as number);
              const panelName = pi?.panelName || `#${panelId}`;
              const matsAgg = aggregateMaterialsForPanel(panelId as number);

              // Totals
              const matsTotals = materialsTotalsByCurrency(panelId as number);
              const hardwareCostEUR = Number.isFinite(pick.summary.cost) ? pick.summary.cost : 0;
              const materialsCostEUR = matsTotals.get("EUR") || 0; // yalnızca EUR grand total'a eklenir
              const grandTotalEUR = hardwareCostEUR + materialsCostEUR;

              return (
                <Stack spacing={2}>
                  <Typography variant="subtitle1" sx={{ fontWeight:700 }}>Selected Equipments</Typography>
                  <Box sx={{ mt:0.5, fontSize:13, color:"#555" }}>
                    <div><strong>Panel:</strong> {panelName}</div>
                    <div><strong>Spare:</strong> {spare}%</div>
                    <div><strong>Total Cost:</strong> {grandTotalEUR.toFixed(2)} EUR</div>
                  </Box>

                  <Typography
                    variant="caption"
                    sx={{ mt: 0.5, display: 'block', color: '#757575', whiteSpace: 'normal' }}
                    >
                    Cost figures are compiled based on UK list pricing data publicly discoverable via Google searches (e.g., “UK Price List”). Provided strictly for information; not a binding offer.
                  </Typography>

                  <Divider />

                  <Section title="Controllers">
  {pick.plc ? (
    <Box sx={{ m: 0 }}>
      1 × <strong>{pick.plc.Brand} {pick.plc.Model}</strong>
      {pick.plc.Description ? <> — {pick.plc.Description}</> : null}
    </Box>
  ) : (
    <Typography variant="body2" sx={{ m: 0 }}>—</Typography>
  )}
</Section>

                  <Section title="I/O Modules">
  {pick.modules.length===0 ? (
    <Typography variant="body2" sx={{ m: 0 }}>—</Typography>
  ) : (
    <Stack spacing={0.25}>
      {(() => {
        const grouped = new Map<string, { m: ModuleRow; qty: number }>();
        for (const x of pick.modules) {
          const key = `${x.item.Brand}|${x.item.Model}|${x.item.Description||""}`;
          if (!grouped.has(key)) grouped.set(key, { m: x.item, qty: 0 });
          grouped.get(key)!.qty += (typeof x.qty === 'number' ? x.qty : 1);
        }
        return Array.from(grouped.values()).map(({m, qty}) => (
          <Box key={`${m.Brand}-${m.Model}`}>{qty} × <strong>{m.Brand} {m.Model}</strong>{m.Description ? <> — {m.Description}</> : null}</Box>
        ));
      })()}
    </Stack>
  )}
</Section>



                  <Section title="Address Keys">
  {!pick.addressKeys || pick.addressKeys.length === 0 ? (
    <Typography variant="body2" sx={{ m: 0 }}>—</Typography>
  ) : (
    <Stack spacing={0.25}>
      {(() => {
        const groupedAK = new Map<string, { m: ModuleRow; qty: number }>();
        for (const x of pick.addressKeys) {
          const key = `${x.item.Brand}|${x.item.Model}|${x.item.Description||""}`;
          if (!groupedAK.has(key)) groupedAK.set(key, { m: x.item, qty: 0 });
          groupedAK.get(key)!.qty += (typeof x.qty === 'number' ? x.qty : 1);
        }
        return Array.from(groupedAK.values()).map(({m, qty}) => (
          <Box key={`ak-${m.Brand}-${m.Model}`}>{qty} × <strong>{m.Brand} {m.Model}</strong>{m.Description ? <> — {m.Description}</> : null}</Box>
        ));
      })()}
    </Stack>
  )}
</Section>

                  <Section title="Power Supplies">
  {pick.powerSupplies && pick.powerSupplies.length > 0 ? (
    <Stack spacing={0.25}>
      {pick.powerSupplies.map((p, i) => (
        <Box key={`psu-${i}`}>{p.qty} × <strong>{p.item.Brand} {p.item.Model}</strong>{p.item.Description ? <> — {p.item.Description}</> : null}</Box>
      ))}
    </Stack>
  ) : (
    <Typography variant="body2" sx={{ m: 0 }}>—</Typography>
  )}
</Section>

                  <Section title="Materials">
  {matsAgg.length===0 ? (
    <Typography variant="body2" sx={{ m: 0 }}>—</Typography>
  ) : (
    <Stack spacing={0.25}>
      {matsAgg.map((x, i) => (
        <Box key={i}>{x.qty} × <strong>{x.label}</strong>{x.desc ? <> — {x.desc}</> : null}</Box>
      ))}
    </Stack>
  )}
</Section>

                  <Section title="Gateways">
  {pick.gateways.length===0 ? (
    <Typography variant="body2" sx={{ m: 0 }}>—</Typography>
  ) : (
    <Stack spacing={0.25}>
      {pick.gateways.map((g,i)=>(
        <Box key={i}>{g.qty} × <strong>{g.item.Brand} {g.item.Model}</strong></Box>
      ))}
    </Stack>
  )}
</Section>

                  
                </Stack>
              );
            })()}
          </Box>
        ) : (
          <Box sx={{ p:2, bgcolor:"#fff", color:"#666" }}>
            Choose a panel and press <strong>Calculate</strong>.
          </Box>
        )}
      </Box>

      {/* Assigned Panels Popup */}
      <Dialog open={assignedOpen} onClose={() => setAssignedOpen(false)} fullWidth maxWidth="lg">
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        Assigned Panels {projectName ? `– ${projectName}` : ""}
        <IconButton onClick={() => setAssignedOpen(false)} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ border: "1px solid #eee", borderRadius: 1, overflow: "hidden" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 13,
                fontFamily: "'Segoe UI','Roboto','Helvetica','Arial',sans-serif",
                color: "#333",
                backgroundColor: "#fff"
              }}
            >
              <thead>
                <tr style={{ backgroundColor: "#1976d2", color: "#fff" }}>
                  {["Panel Name","Located","Controller","Total Cost (EUR)","Actions"].map(h => (
                    <th key={h} style={{ padding: 8, textAlign: "left", borderBottom: "1px solid #e0e0e0" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {savedList.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ padding: 12, color: "#777" }}>
                      No saved hardware found. Select a panel, <strong>Calculate</strong> and then <strong>Save</strong>.
                    </td>
                  </tr>
                ) : (
                  savedList.map((r, i) => (
                    <tr key={r.id} style={{ backgroundColor: i % 2 === 0 ? "#f9f9f9" : "#f0f4f8" }}>
                      <td style={{ padding: 6, borderBottom: "1px solid #f0f0f0", fontWeight: 600 }}>{r.panelName}</td>
                      <td style={{ padding: 6, borderBottom: "1px solid #f0f0f0" }}>{r.located}</td>
                      <td style={{ padding: 6, borderBottom: "1px solid #f0f0f0" }}>{r.controller}</td>
                      <td style={{ padding: 6, borderBottom: "1px solid #f0f0f0" }}>{r.totalEUR.toFixed(2)}</td>
                      <td style={{ padding: 6, borderBottom: "1px solid #f0f0f0" }}>
                        <PrimaryButton onClick={() => handleDeleteSaved(r.id)}>Delete</PrimaryButton>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <PrimaryButton onClick={() => setAssignedOpen(false)}>Close</PrimaryButton>
        </DialogActions>
      </Dialog>

    </Box>
  );
}